import { useEffect, useRef, useState } from 'react';
import { todayKey } from '../../core/dates';
import { currentPeriod, type Period, type PeriodBasis } from '../../core/period';
import type { UserRecord } from '../../data/auth';
import { signOut } from '../../data/auth';
import { savePreferences, withDefaults, type Preferences } from '../../data/preferences';
import { NAV, PHONE_MORE, PHONE_PRIMARY, landingScreen, type ScreenId } from './nav';
import { PeriodSelector } from './PeriodSelector';
import { SyncIndicator } from './SyncIndicator';
import { SettingsScreen } from '../screens/SettingsScreen';
import { BrandingScreen } from '../screens/BrandingScreen';
import { AccountsScreen } from '../screens/AccountsScreen';
import { CardsScreen } from '../screens/CardsScreen';
import { CategoriesScreen } from '../screens/CategoriesScreen';
import { TransactionsScreen } from '../screens/TransactionsScreen';
import { DashboardScreen } from '../screens/DashboardScreen';
import { ImportScreen } from '../screens/ImportScreen';
import { ReclassifyScreen } from '../screens/ReclassifyScreen';
import { ReportsScreen } from '../screens/ReportsScreen';
import { LoansScreen } from '../screens/LoansScreen';
import { ReceivablesScreen } from '../screens/ReceivablesScreen';
import { DueScreen } from '../screens/DueScreen';
import { TasksScreen } from '../screens/TasksScreen';
import { TrashScreen } from '../screens/TrashScreen';
import { AuditScreen } from '../screens/AuditScreen';
import { BackupScreen } from '../screens/BackupScreen';
import { InstallPrompt } from './InstallPrompt';

const PHONE_QUERY = '(max-width: 767px)';

/**
 * Is this a phone-width viewport?
 *
 * The `innerWidth > 0` guard matters: a page can mount before layout — a restored
 * background tab, a PWA launching, an iframe not yet sized — and at width 0 a
 * `max-width` query matches, so the app would conclude "phone". The CSS recovers on the
 * next resize, but WD-08's landing screen is decided once and deliberately does not move
 * afterwards, so it would stay wrong for the whole session.
 *
 * Treating an unmeasured viewport as "not phone" rather than "phone" also fails in the
 * safer direction: desktop is the wider layout, and it degrades gracefully if the guess
 * is wrong.
 */
function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.innerWidth > 0 &&
      window.matchMedia(PHONE_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const sync = () => setIsPhone(window.innerWidth > 0 && mq.matches);
    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  return isPhone;
}

interface Props {
  record: UserRecord;
  preferences?: Partial<Preferences>;
}

export function AppShell({ record, preferences }: Props) {
  const isPhone = useIsPhone();
  const prefs = withDefaults(preferences);
  const today = todayKey();

  const [screen, setScreen] = useState<ScreenId>(() => landingScreen(isPhone));
  const [showMore, setShowMore] = useState(false);
  const [period, setPeriod] = useState<Period>(() => currentPeriod(today, prefs.periodBasis));

  /**
   * Re-apply WD-08 once the viewport is genuinely measured.
   *
   * Covers the remaining case the `innerWidth > 0` guard cannot: mounting unmeasured on
   * a device that turns out to be a phone. Runs at most once, and never after the user
   * has navigated — correcting a landing screen is helpful, moving someone off the screen
   * they chose is not.
   */
  const landingSettled = useRef(typeof window !== 'undefined' && window.innerWidth > 0);
  useEffect(() => {
    if (landingSettled.current || window.innerWidth === 0) return;
    landingSettled.current = true;
    setScreen(landingScreen(isPhone));
  }, [isPhone]);

  /**
   * Persist the basis when it changes, but not the period itself. Which year you were
   * looking at is a transient position; FY-versus-calendar is the remembered preference
   * (FR-SET-01).
   */
  function changePeriod(next: Period) {
    setPeriod(next);
    if (next.basis !== period.basis) {
      void savePreferences(record.uid, { periodBasis: next.basis as PeriodBasis });
    }
  }

  function go(next: ScreenId) {
    landingSettled.current = true;
    setScreen(next);
    setShowMore(false);
  }

  const active = NAV.find((entry) => entry.id === screen);

  return (
    <div className="shell">
      <nav className="sidebar" aria-label="Main">
        <div className="brand">Expenses</div>
        {NAV.map((entry) => (
          <div key={entry.id}>
            {entry.separatorBefore && <div className="nav-sep" />}
            <button
              type="button"
              className="nav-item"
              aria-current={screen === entry.id ? 'page' : undefined}
              onClick={() => go(entry.id)}
              title={entry.label}
            >
              <span className="glyph" aria-hidden="true">
                {entry.glyph}
              </span>
              <span className="text">{entry.label}</span>
            </button>
          </div>
        ))}
      </nav>

      <div className="main">
        <header className="topbar">
          <PeriodSelector period={period} today={today} onChange={changePeriod} />
          <span className="spacer" />
          <SyncIndicator />
          {/* Phone topbar has no room for this; it lives in More instead (§1.2). */}
          <button type="button" className="btn btn-quiet wide-only" onClick={signOut}>
            Sign out
          </button>
        </header>

        <main className="content">
          {showMore ? (
            <>
              <h1>More</h1>
              <ul className="chips">
                {PHONE_MORE.map((entry) => (
                  <li key={entry.id}>
                    <button type="button" className="btn" onClick={() => go(entry.id)}>
                      {entry.glyph} {entry.label}
                    </button>
                  </li>
                ))}
                <li>
                  <button type="button" className="btn" onClick={signOut}>
                    ⏻ Sign out
                  </button>
                </li>
              </ul>
            </>
          ) : (
            <>
              {screen === 'settings' ? (
                <SettingsScreen record={record} />
              ) : screen === 'branding' ? (
                <BrandingScreen />
              ) : screen === 'accounts' ? (
                <AccountsScreen record={record} />
              ) : screen === 'cards' ? (
                <CardsScreen record={record} />
              ) : screen === 'categories' ? (
                <CategoriesScreen record={record} />
              ) : screen === 'trash' ? (
                <TrashScreen record={record} />
              ) : screen === 'audit' ? (
                <AuditScreen />
              ) : screen === 'backup' ? (
                <BackupScreen record={record} />
              ) : screen === 'due' ? (
                <DueScreen record={record} />
              ) : screen === 'tasks' ? (
                <TasksScreen record={record} />
              ) : screen === 'loans' ? (
                <LoansScreen record={record} />
              ) : screen === 'receivables' ? (
                <ReceivablesScreen record={record} />
              ) : screen === 'reports' ? (
                <ReportsScreen period={period} />
              ) : screen === 'reclassify' ? (
                <ReclassifyScreen record={record} period={period} />
              ) : screen === 'import' ? (
                <ImportScreen record={record} />
              ) : screen === 'dashboard' ? (
                <DashboardScreen record={record} period={period} />
              ) : screen === 'transactions' || screen === 'add' ? (
                <TransactionsScreen
                  record={record}
                  period={period}
                  isPhone={isPhone}
                  startInEntry={screen === 'add'}
                />
              ) : (
                <>
                  <h1>{active?.label ?? 'Expenses'}</h1>
                  <p className="muted">
                    Signed in as {record.displayName ?? record.email}
                    {' · '}
                    {period.basis === 'fy' ? 'financial year' : 'calendar year'} basis
                  </p>
                  <div className="placeholder">
                    <strong>{active?.label}</strong> arrives in a later milestone.
                    <br />
                    M1 delivers the shell, sign-in, settings and branding.
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>

      <nav className="bottombar" aria-label="Main">
        {PHONE_PRIMARY.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-current={screen === entry.id && !showMore ? 'page' : undefined}
            onClick={() => go(entry.id)}
          >
            <span className="glyph" aria-hidden="true">
              {entry.glyph}
            </span>
            {entry.label}
          </button>
        ))}
        <button type="button" aria-current={showMore ? 'page' : undefined} onClick={() => setShowMore(true)}>
          <span className="glyph" aria-hidden="true">
            ⋯
          </span>
          More
        </button>
      </nav>

      <InstallPrompt />
    </div>
  );
}
